import { inject, NgModule, Type } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  ResolveFn,
  Route,
  Router,
  RouterModule,
  RouterStateSnapshot,
  Routes,
} from '@angular/router';
import { CreateTicketComponent } from '@app/components/create-ticket/create-ticket.component';
import { LegalPersonComponent } from '@app/components/profile/legal-person/legal-person/legal-person.component';
import { ProjectIdeasComponent } from '@app/components/profile/legal-person/project-ideas/project-ideas.component';
import { RepresentativeDocumentsComponent } from '@app/components/profile/legal-person/representative/representative.component';
import { RepresentativesComponent } from '@app/components/profile/legal-person/representatives/representatives.component';
import { PersonDataComponent } from '@app/components/profile/naturalPerson/person-data/person-data.component';
import { ProjectsWithFiltersComponent } from '@app/components/projects-with-filters/projects-with-filters.component';
import { DefaultProjectStatusId } from '@app/helpers/project-state.helper';
import { BreadcrumbItem } from '@app/models/app.model';
import { AddNewProfileWizardComponent } from '@app/pages/add-new-profile-wizard/add-new-profile-wizard.component';
import { ApprovalRequestsComponent } from '@app/pages/admin/approval-requests/approval-requests.component';
import { CompanyTypeAddComponent } from '@app/pages/admin/database/company-type/company-type-add/company-type-add.component';
import { CompanyTypeDetailsComponent } from '@app/pages/admin/database/company-type/company-type-details/company-type-details.component';
import { CompanyTypeListComponent } from '@app/pages/admin/database/company-type/company-type-list/company-type-list.component';
import { CountryAddComponent } from '@app/pages/admin/database/country/country-add/country-add.component';
import { CountryDetailsComponent } from '@app/pages/admin/database/country/country-details/country-details.component';
import { CountryListComponent } from '@app/pages/admin/database/country/country-list/country-list.component';
import { CurrencyAddComponent } from '@app/pages/admin/database/currency/currency-add/currency-add.component';
import { CurrencyDetailsComponent } from '@app/pages/admin/database/currency/currency-details/currency-details.component';
import { CurrencyListComponent } from '@app/pages/admin/database/currency/currency-list/currency-list.component';
import { LegalEntityRoleAddComponent } from '@app/pages/admin/database/legal-entity-role/legal-entity-role-add/legal-entity-role-add.component';
import { LegalEntityRoleDetailsComponent } from '@app/pages/admin/database/legal-entity-role/legal-entity-role-details/legal-entity-role-details.component';
import { LegalEntityRoleListComponent } from '@app/pages/admin/database/legal-entity-role/legal-entity-role-list/legal-entity-role-list.component';
import { NameTitleAddComponent } from '@app/pages/admin/database/name-title/name-title-add/name-title-add.component';
import { NameTitleDetailsComponent } from '@app/pages/admin/database/name-title/name-title-details/name-title-details.component';
import { NameTitleListComponent } from '@app/pages/admin/database/name-title/name-title-list/name-title-list.component';
import { PersonRoleAddComponent } from '@app/pages/admin/database/person-role/person-role-add/person-role-add.component';
import { PersonRoleDetailsComponent } from '@app/pages/admin/database/person-role/person-role-details/person-role-details.component';
import { PersonRoleListComponent } from '@app/pages/admin/database/person-role/person-role-list/person-role-list.component';
import { ProjectClassificationAddComponent } from '@app/pages/admin/database/project-classification/project-classification-add/project-classification-add.component';
import { ProjectClassificationDetailsComponent } from '@app/pages/admin/database/project-classification/project-classification-details/project-classification-details.component';
import { ProjectClassificationListComponent } from '@app/pages/admin/database/project-classification/project-classification-list/project-classification-list.component';
import { ContactDetailsComponent } from '@app/pages/admin/entities/contacts/contact-details/contact-details.component';
import { ContactListComponent } from '@app/pages/admin/entities/contacts/contact-list/contact-list.component';
import { InvestorDetailsComponent } from '@app/pages/admin/entities/investors/investor-details/investor-details.component';
import { InvestorListComponent } from '@app/pages/admin/entities/investors/investor-list/investor-list.component';
import { LegalEntityDetailsComponent } from '@app/pages/admin/entities/legal-entities/legal-entity-details/legal-entity-details.component';
import { LegalEntityListComponent } from '@app/pages/admin/entities/legal-entities/legal-entity-list/legal-entity-list.component';
import { UserDetailsComponent } from '@app/pages/admin/entities/users/user-details/user-details.component';
import { UserListComponent } from '@app/pages/admin/entities/users/user-list/user-list.component';
import { AdminProjectDetailsComponent } from '@app/pages/admin/projects/all-projects/project-details/project-details.component';
import { AdminProjectEditComponent } from '@app/pages/admin/projects/all-projects/project-edit/project-edit.component';
import { AdminProjectListComponent } from '@app/pages/admin/projects/all-projects/project-list/project-list.component';
import { InvestmentListComponent } from '@app/pages/admin/projects/investment-list/investment-list.component';
import { ProjectIdeaEditComponent } from '@app/pages/admin/projects/pending-project-ideas/project-idea-edit/project-idea-edit.component';
import { ProjectIdeasDetailsComponent } from '@app/pages/admin/projects/pending-project-ideas/project-ideas-details/project-ideas-details.component';
import { ProjectIdeasListComponent } from '@app/pages/admin/projects/pending-project-ideas/project-ideas-list/project-ideas-list.component';
import { LogoutComponent } from '@app/pages/auth/logout/logout.component';
import { ErrorComponent } from '@app/pages/error/error.component';
import { MeetingOfferResponseComponent } from '@app/pages/meeting-offer-response/meeting-offer-response.component';
import { MeetingResponseComponent } from '@app/pages/meeting-response/meeting-response.component';
import { LegalPersonProfileComponent } from '@app/pages/profile/legal-person-profile/legal-person-profile.component';
import { NaturalPersonProfileComponent } from '@app/pages/profile/natural-person-profile/natural-person-profile.component';
import { ProjectDetailsComponent } from '@app/pages/project-details/project-details.component';
import { ProjectOffersDetailsComponent } from '@app/pages/project-offers/project-offers-details/project-offers-details.component';
import { ProjectService } from '@app/services/projects/service';
import { AppStore } from '@app/stores/app.store';
import { AuthStore } from '@app/stores/auth.store';
import { isObservable, map, Observable, of } from 'rxjs';
import { ConfirmEmailComponent } from 'src/app/pages/auth/confirm-email/confirm-email.component';
import { EnableTwoFactorAuthComponent } from 'src/app/pages/auth/enable-two-factor-auth/enable-two-factor-auth.component';
import { DemoPageComponent } from 'src/app/pages/demo-page/demo-page.component';
import { environment } from 'src/environments/environment';
import {
  adminGuard,
  authGuard,
  basicRegistrationGuard,
  legalPersonGuard,
} from '../+guards/auth.guard';
import {
  emailLinkRouteGuard,
  emailLinkSubaccountGuard,
} from '../+guards/email-link.guard';
import { notQualifiedCompanyGuard } from '../+guards/not-qualified-company.guard';
import { ForgottPasswordChangeComponent } from '../pages/auth/forgott-password-change/forgott-password-change.component';
import { ForgottPasswordComponent } from '../pages/auth/forgott-password/forgott-password.component';
import { LoginComponent } from '../pages/auth/login/login.component';
import { RegistrationComponent } from '../pages/auth/registration/registration.component';
import { TwoFactorAuthComponent } from '../pages/auth/two-factor-auth/two-factor-auth.component';
import { InvestmentQualificationWizardComponent } from '../pages/investor-qualification-wizard/investor-qualification-wizard.component';
import { NewProjectIdeaComponent } from '../pages/new-project-idea/new-project-idea.component';
import { ProfileComponent } from '../pages/profile/profile.component';
import { ProjectWizardComponent } from '../pages/project-wizard/project-wizard.component';
import { ProjectsComponent } from '../pages/projects/projects.component';
import { RegistrationWizardComponent } from '../pages/registration-wizard/registration-wizard.component';
import { AdminLayoutComponent } from './admin-layout/admin-layout.component';
import { MainLayoutComponent } from './main-layout/main-layout.component';

const profileRedirectGuard: CanActivateFn = (route, state) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  if (state.url === '/profile' || !route.firstChild) {
    if (authStore.loggedInCompanyId()) {
      return router.createUrlTree(['/', 'profile', 'legal']);
    } else {
      return router.createUrlTree(['/', 'profile', 'natural']);
    }
  }

  return true;
};

const routes: Routes = [
  {
    path: '',
    redirectTo: 'projects',
    pathMatch: 'full',
  },
  {
    path: 'meeting/respond',
    loadComponent: () => MeetingResponseComponent,
    canActivate: [authGuard, basicRegistrationGuard, profileRedirectGuard],
    resolve: {
      breadcrumbResolver: appDataResolver(() => [], undefined),
    },
  },
  {
    path: 'accept-meeting',
    loadComponent: () => MeetingOfferResponseComponent,
    canActivate: [authGuard, basicRegistrationGuard, profileRedirectGuard],
    resolve: {
      breadcrumbResolver: appDataResolver(() => [], undefined),
    },
  },
  {
    path: 'embed/projects',
    loadComponent: () =>
      import('@app/pages/embed/projects-embed/projects-embed.component').then(
        (m) => m.ProjectsEmbedComponent,
      ),
    resolve: {
      breadcrumbResolver: appDataResolver(() => [], undefined),
    },
  },
  {
    path: 'auth',
    component: MainLayoutComponent,
    resolve: {
      breadcrumbResolver: appDataResolver(() => [], undefined),
    },
    children: [
      { path: 'login', component: LoginComponent },
      {
        path: '2fa',
        component: TwoFactorAuthComponent,
      },
      { path: 'registration', component: RegistrationComponent },
      { path: 'forgot-password', component: ForgottPasswordComponent },
      {
        path: 'forgot-password-change',
        component: ForgottPasswordChangeComponent,
      },
      { path: 'confirm-email', component: ConfirmEmailComponent },
      { path: 'logout', component: LogoutComponent },
    ],
  },
  { path: 'confirm-email', component: ConfirmEmailComponent },
  {
    path: 'password-reset',
    component: ForgottPasswordChangeComponent,
  },
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      {
        path: 'projects',
        component: ProjectsComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            (route, state) => [{ text: 'breadcrumb.projects' }],
            undefined,
          ),
        },
      },
      {
        path: 'projects/my',
        component: ProjectsComponent,
        canActivate: [emailLinkSubaccountGuard, emailLinkRouteGuard],
        data: { emailLinkTarget: 'projectsMy' },
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.projects', url: '/projects' }],
            undefined,
          ),
        },
      },
      {
        path: 'projects/investments',
        component: ProjectsComponent,
        canActivate: [emailLinkSubaccountGuard, emailLinkRouteGuard],
        data: { emailLinkTarget: 'projectsInvestments' },
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.projects', url: '/projects' }],
            undefined,
          ),
        },
      },
      {
        path: 'project/:id',
        component: ProjectDetailsComponent,
        canActivate: [emailLinkSubaccountGuard, notQualifiedCompanyGuard],
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              {
                text: 'breadcrumb.projects',
                url: '/projects',
                queryParams: { type: 'public' },
              },
            ],
            (route) => {
              const id = route.params['id'] as string;
              const projectService = inject(ProjectService);
              return projectService
                .getProjectById(id)
                .pipe(
                  map((project) => [
                    getProjectTypeBreadcrumbByState(project.state),
                    { text: project.name },
                  ]),
                );
            },
          ),
        },
      },
      {
        path: 'project/:id/offers',
        component: ProjectsComponent,
        canActivate: [emailLinkSubaccountGuard, emailLinkRouteGuard],
        data: { emailLinkTarget: 'projectOffers' },
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.projects', url: '/projects' }],
            undefined,
          ),
        },
      },
      {
        path: 'project/offers/pending',
        component: ProjectsComponent,
        canActivate: [emailLinkSubaccountGuard, emailLinkRouteGuard],
        data: { emailLinkTarget: 'projectOffersPending' },
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.projects', url: '/projects' }],
            undefined,
          ),
        },
      },
      {
        path: 'project/:id/offers/details',
        component: ProjectOffersDetailsComponent,
        canActivate: [notQualifiedCompanyGuard],
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              { text: 'general.home', url: '/projects' },
              { text: 'breadcrumb.profile', url: '/profile' },
              {
                text: 'profile.myProjects.title',
                url: '/profile/legal/myProjects.fundraising',
              },
              {
                text: 'profile.myProjects.fundraising',
                url: '/profile/legal/myProjects.fundraising',
              },
            ],
            (route) => {
              const projectService = inject(ProjectService);
              const projectId = route.paramMap.get('id') ?? '';
              if (!projectId) {
                return { text: 'breadcrumb.details' };
              }

              return projectService
                .getProjectById(projectId)
                .pipe(map((project) => ({ text: project.name ?? projectId })));
            },
          ),
        },
      },
      {
        path: 'error',
        component: ErrorComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(() => [], undefined),
        },
      },
      {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [
          authGuard,
          basicRegistrationGuard,
          profileRedirectGuard,
          notQualifiedCompanyGuard,
        ],
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.profile', url: '/profile' }],
            undefined,
          ),
        },
      },
      {
        path: 'profile/natural',
        component: NaturalPersonProfileComponent,
        canActivate: [
          authGuard,
          basicRegistrationGuard,
          notQualifiedCompanyGuard,
        ],
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.profile', url: '/profile' }],
            undefined,
          ),
        },
        children: [
          { path: '', redirectTo: 'personal-info', pathMatch: 'full' },
          { path: 'personal-info', component: PersonDataComponent },
          {
            path: 'investments',
            redirectTo: 'investments.accepted',
            pathMatch: 'full',
          },
          {
            path: 'investments.accepted',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'acceptedInvestments' },
          },
          {
            path: 'investments.pending',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'pendingInvestments' },
          },
          {
            path: 'investments.rejected',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'declinedInvestments' },
          },
          {
            path: 'watched-projects',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'followed' },
          },
          {
            path: 'support-request',
            component: CreateTicketComponent,
            data: { ticketType: 'complaint' },
          },
          {
            path: 'message',
            component: CreateTicketComponent,
            data: { ticketType: 'message' },
          },
        ],
      },
      {
        path: 'profile/legal',
        component: LegalPersonProfileComponent,
        canActivate: [authGuard, basicRegistrationGuard],
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.profile', url: '/profile' }],
            undefined,
          ),
        },
        children: [
          {
            path: '',
            redirectTo: 'personalInfo.legalPerson',
            pathMatch: 'full',
          },
          { path: 'personalInfo.legalPerson', component: LegalPersonComponent },
          {
            path: 'personalInfo.representative',
            component: RepresentativesComponent,
          },
          {
            path: 'personalInfo.documents',
            component: RepresentativeDocumentsComponent,
          },
          {
            path: 'myProjects.fundraising',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'myRunningProjects' },
          },
          { path: 'myProjects.submitted', component: ProjectIdeasComponent },
          {
            path: 'myProjects.inPreparation',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'myUnderPreparationProjects' },
          },
          {
            path: 'myProjects.underReview',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'myUnderVerificationProjects' },
          },
          {
            path: 'investments.accepted',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'acceptedInvestments' },
          },
          {
            path: 'investments.pending',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'pendingInvestments' },
          },
          {
            path: 'investments.rejected',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'declinedInvestments' },
          },
          {
            path: 'watchedProjects',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'followed' },
          },
          {
            path: 'watchedProjects.inPreparation',
            component: ProjectsWithFiltersComponent,
            data: { displayType: 'followedUnderPreparation' },
          },
          {
            path: 'complaint',
            component: CreateTicketComponent,
            data: { ticketType: 'complaint' },
          },
          {
            path: 'message',
            component: CreateTicketComponent,
            data: { ticketType: 'message' },
          },
        ],
      },
      {
        path: 'add-new-profile/:type',
        component: AddNewProfileWizardComponent,
        canActivate: [notQualifiedCompanyGuard],
      },
      {
        path: 'new-project-idea',
        component: NewProjectIdeaComponent,
        canActivate: [authGuard, legalPersonGuard],
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              { text: 'breadcrumb.profile', url: '/profile' },
              { text: 'breadcrumb.newProjectIdea', url: '/new-project-idea' },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'project-wizard/:id',
        component: ProjectWizardComponent,
        canActivate: [authGuard, legalPersonGuard],
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              { text: 'breadcrumb.projectWizard', url: '/project-wizard' },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'investor-qualification-test',
        component: InvestmentQualificationWizardComponent,
        canActivate: [authGuard, basicRegistrationGuard],
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              { text: 'breadcrumb.profile', url: '/profile' },
              {
                text: 'breadcrumb.investorQualification',
                url: '/investor-qualification-test',
              },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'auth/2fa/enable',
        component: EnableTwoFactorAuthComponent,
        canActivate: [authGuard],
        resolve: {
          breadcrumbResolver: appDataResolver(() => [], undefined),
        },
      },
      {
        path: 'auth/registration/wizard',
        component: RegistrationWizardComponent,
        canActivate: [authGuard],
        resolve: {
          breadcrumbResolver: appDataResolver(() => [], undefined),
        },
      },
      {
        path: 'auth/registration/cancel',
        component: ProfileComponent,
        canActivate: [authGuard],
        resolve: {
          breadcrumbResolver: appDataResolver(() => [], undefined),
        },
      },
    ],
  },
  ...(!environment.production
    ? [
        {
          path: 'demo',
          component: MainLayoutComponent,
          resolve: {
            breadcrumbResolver: appDataResolver(
              () => [{ text: 'breadcrumb.demo', url: '/demo' }],
              undefined,
            ),
          },
          children: [{ path: '', component: DemoPageComponent }],
        },
      ]
    : []),

  {
    path: 'admin',
    component: AdminLayoutComponent,
    canActivate: [authGuard, adminGuard],
    canActivateChild: [authGuard, adminGuard],
    children: [
      {
        path: '',
        redirectTo: 'approvals',
        pathMatch: 'full',
      },
      {
        path: '',
        redirectTo: 'users',
        pathMatch: 'full',
      },
      {
        path: 'approvals',
        component: ApprovalRequestsComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.approvals', url: '/admin/approvals' }],
            undefined,
          ),
        },
      },
      {
        path: 'projects',
        component: AdminProjectListComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              {
                text: 'breadcrumb.admin',
                url: '/admin/projects',
              },
              {
                text: 'breadcrumb.projects',
              },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'projects/:id',
        component: AdminProjectDetailsComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              {
                text: 'breadcrumb.projects',
                url: '/admin/projects/:id',
              },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'projects/:id/edit',
        component: AdminProjectEditComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              { text: 'breadcrumb.edit', url: '/admin/projects/:id/edit' },
            ],
            undefined,
          ),
        },
        data: { hideSidebar: true },
      },
      {
        path: 'project-ideas',
        component: ProjectIdeasListComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              {
                text: 'breadcrumb.pendingProjectIdeas',
                url: '/admin/project-ideas',
              },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'project-ideas/:id',
        component: ProjectIdeasDetailsComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              { text: 'breadcrumb.details', url: '/admin/project-ideas/:id' },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'project-ideas/:id/edit',
        component: ProjectIdeaEditComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              { text: 'breadcrumb.edit', url: '/admin/project-ideas/:id/edit' },
            ],
            undefined,
          ),
        },
        data: { hideSidebar: true },
      },
      {
        path: 'investments',
        component: InvestmentListComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              {
                text: 'breadcrumb.investments',
                url: '/admin/investments',
              },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'users',
        component: UserListComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.users', url: '/admin/users' }],
            undefined,
          ),
        },
      },
      {
        path: 'users/:id',
        component: UserDetailsComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.details', url: '/admin/users/:id' }],
            undefined,
          ),
        },
      },
      {
        path: 'legal-entities',
        component: LegalEntityListComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              {
                text: 'admin.sideNav.entities.legalEntities',
                url: '/admin/legal-entities',
              },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'legal-entities/:id',
        component: LegalEntityDetailsComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              { text: 'breadcrumb.details', url: '/admin/legal-entities/:id' },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'investors',
        component: InvestorListComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              { text: 'general.admin', url: '/admin' },
              {
                text: 'admin.sideNav.entities.investors',
                url: '/admin/investors',
              },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'investors/:id',
        component: InvestorDetailsComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.details', url: '/admin/investors/:id' }],
            undefined,
          ),
        },
      },
      {
        path: 'contacts',
        component: ContactListComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [
              { text: 'general.admin', url: '/admin' },
              {
                text: 'admin.sideNav.entities.contacts',
                url: '/admin/contacts',
              },
            ],
            undefined,
          ),
        },
      },
      {
        path: 'contacts/:id',
        component: ContactDetailsComponent,
        resolve: {
          breadcrumbResolver: appDataResolver(
            () => [{ text: 'breadcrumb.details', url: '/admin/contacts/:id' }],
            undefined,
          ),
        },
      },
      getDatabaseManagementChild(),
    ],
  },
  { path: '**', redirectTo: 'projects' },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class LayoutsRoutingModule {}

function getDatabaseManagementChild(): Route {
  return {
    path: 'database',
    children: [
      ...getDatabaseManagementItemsForType(
        'companyType',
        CompanyTypeListComponent,
        'Company types',
        CompanyTypeDetailsComponent,
        'companyTypeAdd',
        CompanyTypeAddComponent,
      ),
      ...getDatabaseManagementItemsForType(
        'country',
        CountryListComponent,
        'Countries',
        CountryDetailsComponent,
        'countryAdd',
        CountryAddComponent,
      ),
      ...getDatabaseManagementItemsForType(
        'currency',
        CurrencyListComponent,
        'Currencies',
        CurrencyDetailsComponent,
        'currencyAdd',
        CurrencyAddComponent,
      ),
      ...getDatabaseManagementItemsForType(
        'legalEntityRole',
        LegalEntityRoleListComponent,
        'Legal entity roles',
        LegalEntityRoleDetailsComponent,
        'legalEntityRoleAdd',
        LegalEntityRoleAddComponent,
      ),
      ...getDatabaseManagementItemsForType(
        'nameTitle',
        NameTitleListComponent,
        'Name titles',
        NameTitleDetailsComponent,
        'nameTitleAdd',
        NameTitleAddComponent,
      ),
      ...getDatabaseManagementItemsForType(
        'personRole',
        PersonRoleListComponent,
        'Person roles',
        PersonRoleDetailsComponent,
        'personRoleAdd',
        PersonRoleAddComponent,
      ),
      ...getDatabaseManagementItemsForType(
        'projectClassification',
        ProjectClassificationListComponent,
        'Project classifications',
        ProjectClassificationDetailsComponent,
        'projectClassificationAdd',
        ProjectClassificationAddComponent,
      ),
    ],
  };
}

function getDatabaseManagementItemsForType(
  dataType: string,
  listComponent: Type<any>,
  listTitle: string,
  detailsComponent: Type<any>,
  addElementRoute: string,
  addComponent: Type<any>,
) {
  return [
    {
      path: dataType,
      component: listComponent,
      resolve: {
        breadcrumbResolver: appDataResolver(
          () => [
            { text: 'general.admin', url: '/admin' },
            { text: listTitle, url: `/admin/database/${dataType}` },
          ],
          undefined,
        ),
      },
    },
    {
      path: dataType + '/addNew',
      component: addComponent,
      resolve: {
        breadcrumbResolver: appDataResolver(
          () => [
            { text: 'general.admin', url: '/admin' },
            { text: listTitle, url: `/admin/database/${addElementRoute}` },
          ],
          undefined,
        ),
      },
    },
    {
      path: dataType + '/:id',
      component: detailsComponent,
      resolve: {
        breadcrumbResolver: appDataResolver(
          () => [
            { text: 'general.admin', url: '/admin' },
            { text: listTitle, url: `/admin/database/${dataType}` },
          ],
          undefined,
        ),
      },
    },
  ];
}

function appDataResolver(
  itemsGetter: (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ) => BreadcrumbItem[],
  breadcrumbLastItemGetter: // can and should be used for cases where you need to get route based custom info (eg.: breadcrumb contains some dynamic name based on an entity id coming from url path)
    | ((
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
      ) =>
        | BreadcrumbItem
        | BreadcrumbItem[]
        | Observable<BreadcrumbItem | BreadcrumbItem[]>)
    | undefined,
): ResolveFn<BreadcrumbItem[]> {
  return (route, state) => {
    const baseItems = [...itemsGetter(route, state)];
    const appStore = inject(AppStore);

    if (breadcrumbLastItemGetter) {
      const additionalItems = breadcrumbLastItemGetter(route, state);
      const additionalItems$ = isObservable(additionalItems)
        ? additionalItems
        : of(additionalItems);
      return additionalItems$.pipe(
        map((extra) => {
          const normalizedExtra = Array.isArray(extra) ? extra : [extra];
          const itemsToAdd = [...baseItems, ...normalizedExtra];
          appStore.updateBreadcrumbs(itemsToAdd);
          return itemsToAdd;
        }),
      );
    }

    appStore.updateBreadcrumbs(baseItems);
    return of(baseItems);
  };
}

function getProjectTypeBreadcrumbByState(state: number): BreadcrumbItem {
  if (state >= DefaultProjectStatusId.ClosedCollectionDone) {
    return {
      text: 'projects.closedProjects',
      url: '/projects',
      queryParams: { type: 'closed' },
    };
  }
  if (state >= DefaultProjectStatusId.Published) {
    return {
      text: 'projects.actualProjects',
      url: '/projects',
      queryParams: { type: 'public' },
    };
  }
  return {
    text: 'projects.projectsInPreparation',
    url: '/projects',
    queryParams: { type: 'underPreparation' },
  };
}
